import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Input } from './ui/input.js';
import { trpc } from '../lib/trpc.js';

type DayRow = {
  dateStr: string; // YYYY-MM-DD
  dayOfWeek: string;
  isWorkDay: boolean;
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  breakMinutes: number;
};

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function generateDaysBetween(startStr: string, endStr: string): DayRow[] {
  const result: DayRow[] = [];
  const curr = new Date(startStr);
  const end = new Date(endStr);

  while (curr <= end) {
    const yyyy = curr.getFullYear();
    const mm = String(curr.getMonth() + 1).padStart(2, '0');
    const dd = String(curr.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const dayOfWeek = DAY_NAMES[curr.getDay()] ?? '';
    const isWeekend = curr.getDay() === 0 || curr.getDay() === 6;

    result.push({
      dateStr,
      dayOfWeek,
      isWorkDay: !isWeekend,
      startTime: '08:00',
      endTime: '16:30',
      breakMinutes: 30,
    });

    curr.setDate(curr.getDate() + 1);
  }
  return result;
}

function calculateDayHours(r: DayRow): number {
  if (!r.isWorkDay || !r.startTime || !r.endTime) return 0;
  const [sH, sM] = r.startTime.split(':').map(Number);
  const [eH, eM] = r.endTime.split(':').map(Number);
  if (sH == null || sM == null || eH == null || eM == null) return 0;

  const startTotalMinutes = sH * 60 + sM;
  let endTotalMinutes = eH * 60 + eM;
  if (endTotalMinutes <= startTotalMinutes) {
    endTotalMinutes += 24 * 60; // Mitternachtssprung
  }

  const grossMinutes = endTotalMinutes - startTotalMinutes;
  const netMinutes = Math.max(0, grossMinutes - (r.breakMinutes || 0));
  return Math.round((netMinutes / 60) * 100) / 100;
}

export function TimesheetEntryEditor({
  timesheetId,
  periodStart,
  periodEnd,
  timezone = 'Europe/Berlin',
  readOnly = false,
}: {
  timesheetId: string;
  periodStart: string;
  periodEnd: string;
  timezone?: string;
  readOnly?: boolean;
}) {
  const utils = trpc.useUtils();
  const existingEntries = trpc.timesheets.listEntries.useQuery({ timesheetId });
  const preview = trpc.surchargeRuleSets.previewForTimesheet.useQuery(
    { timesheetId },
    { enabled: !!timesheetId },
  );

  const setEntriesMutation = trpc.timesheets.setEntries.useMutation({
    onSuccess: () => {
      toast.success('Tageszeilen gespeichert & Stunden aktualisiert.');
      utils.timesheets.listMine.invalidate();
      utils.timesheets.listEntries.invalidate({ timesheetId });
      utils.surchargeRuleSets.previewForTimesheet.invalidate({ timesheetId });
    },
    onError: (e) => toast.error(e.message),
  });

  const [days, setDays] = useState<DayRow[]>(() =>
    generateDaysBetween(periodStart, periodEnd),
  );

  // Vorgefüllte Tageszeilen falls vorhanden
  const hasInitialized = useState(false);
  if (existingEntries.data && existingEntries.data.length > 0 && !hasInitialized[0]) {
    hasInitialized[1](true);
    // map existing entries to days
    const newDays = generateDaysBetween(periodStart, periodEnd).map((day) => {
      const workEntry = existingEntries.data?.find(
        (e) => e.entryType === 'work' && e.startAt.toString().startsWith(day.dateStr),
      );
      const breakEntry = existingEntries.data?.find(
        (e) => e.entryType === 'break' && e.startAt.toString().startsWith(day.dateStr),
      );
      if (workEntry) {
        const sDate = new Date(workEntry.startAt);
        const eDate = new Date(workEntry.endAt);
        const sTime = `${String(sDate.getHours()).padStart(2, '0')}:${String(sDate.getMinutes()).padStart(2, '0')}`;
        const eTime = `${String(eDate.getHours()).padStart(2, '0')}:${String(eDate.getMinutes()).padStart(2, '0')}`;
        let breakMins = 0;
        if (breakEntry) {
          const bSDate = new Date(breakEntry.startAt);
          const bEDate = new Date(breakEntry.endAt);
          breakMins = Math.round((bEDate.getTime() - bSDate.getTime()) / (1000 * 60));
        }
        return {
          ...day,
          isWorkDay: true,
          startTime: sTime,
          endTime: eTime,
          breakMinutes: breakMins,
        };
      }
      return { ...day, isWorkDay: false };
    });
    setDays(newDays);
  }

  const updateDay = (index: number, patch: Partial<DayRow>) => {
    setDays((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const totalCalculatedHours = days.reduce((sum, d) => sum + calculateDayHours(d), 0);

  const handleSave = () => {
    const payloadEntries: Array<{
      entryType: 'work' | 'break';
      startAt: string;
      endAt: string;
      notes?: string;
    }> = [];

    for (const d of days) {
      if (!d.isWorkDay) continue;
      // build ISO String with timezone offset
      const workStartIso = new Date(`${d.dateStr}T${d.startTime}:00`).toISOString();
      const workEndIso = new Date(`${d.dateStr}T${d.endTime}:00`).toISOString();

      payloadEntries.push({
        entryType: 'work',
        startAt: workStartIso,
        endAt: workEndIso,
      });

      if (d.breakMinutes > 0) {
        // Pausen-Intervall (z.B. mittig in der Arbeitszeit)
        const sDate = new Date(`${d.dateStr}T${d.startTime}:00`);
        const breakStart = new Date(sDate.getTime() + 4 * 60 * 60 * 1000); // 4h nach Start
        const breakEnd = new Date(breakStart.getTime() + d.breakMinutes * 60 * 1000);

        payloadEntries.push({
          entryType: 'break',
          startAt: breakStart.toISOString(),
          endAt: breakEnd.toISOString(),
        });
      }
    }

    setEntriesMutation.mutate({
      timesheetId,
      entries: payloadEntries,
    });
  };

  return (
    <Card className="border-[var(--border)]">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span>📅 Tageserfassung ({periodStart} bis {periodEnd})</span>
          <span className="text-base font-bold text-[var(--primary)]">
            Summe: {totalCalculatedHours.toFixed(2)} h
          </span>
        </CardTitle>
        <CardDescription>
          Trage Arbeitszeiten &amp; Pausen je Tag ein. Die Zuschläge werden automatisch berechnet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 border rounded-md p-3 bg-[var(--card)] max-h-96 overflow-y-auto">
          {days.map((d, i) => {
            const dayHours = calculateDayHours(d);
            return (
              <div
                key={d.dateStr}
                className={`flex flex-wrap items-center justify-between gap-2 p-2 rounded-md ${
                  d.isWorkDay ? 'bg-[var(--muted)]/20' : 'opacity-60'
                }`}
              >
                <div className="flex items-center gap-3 min-w-[10rem]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded accent-[var(--primary)]"
                    checked={d.isWorkDay}
                    disabled={readOnly}
                    onChange={(e) => updateDay(i, { isWorkDay: e.target.checked })}
                  />
                  <div>
                    <span className="font-semibold text-sm">
                      {d.dayOfWeek}, {d.dateStr}
                    </span>
                  </div>
                </div>

                {d.isWorkDay ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <label className="text-xs text-[var(--muted-foreground)]">Von:</label>
                    <Input
                      type="time"
                      className="w-28 h-8 text-xs"
                      value={d.startTime}
                      disabled={readOnly}
                      onChange={(e) => updateDay(i, { startTime: e.target.value })}
                    />
                    <label className="text-xs text-[var(--muted-foreground)]">Bis:</label>
                    <Input
                      type="time"
                      className="w-28 h-8 text-xs"
                      value={d.endTime}
                      disabled={readOnly}
                      onChange={(e) => updateDay(i, { endTime: e.target.value })}
                    />
                    <label className="text-xs text-[var(--muted-foreground)]">Pause (Min):</label>
                    <Input
                      type="number"
                      min={0}
                      step={5}
                      className="w-20 h-8 text-xs"
                      value={d.breakMinutes}
                      disabled={readOnly}
                      onChange={(e) => updateDay(i, { breakMinutes: Number(e.target.value) })}
                    />
                    <span className="font-medium text-xs w-16 text-right">
                      {dayHours.toFixed(2)} h
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-[var(--muted-foreground)] italic">Frei</span>
                )}
              </div>
            );
          })}
        </div>

        {!readOnly && (
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={setEntriesMutation.isPending}
              onClick={handleSave}
            >
              {setEntriesMutation.isPending ? 'Speichere Zeiten …' : 'Tageszeilen speichern'}
            </Button>
          </div>
        )}

        {/* Live-Zuschlagsvorschau */}
        {preview.data && (
          <div className="rounded-md border border-[oklch(0.85_0.12_145)] bg-[oklch(0.97_0.03_145)] p-4 space-y-3 text-sm text-[oklch(0.25_0.1_145)]">
            <div className="flex items-center justify-between font-bold text-base border-b border-[oklch(0.85_0.12_145)] pb-2">
              <span>⚡ Live-Zuschlagsvorschau (Engine OPS-69)</span>
              <span>Netto-Stunden: {preview.data.netHours.toFixed(2)} h</span>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <div className="font-semibold text-xs text-[var(--muted-foreground)] uppercase">
                  Lohnseite (Vergütung Worker)
                </div>
                <div className="text-base font-bold">
                  Zuschläge: {(preview.data.wageSurchargeCents / 100).toFixed(2)} €
                </div>
                {preview.data.wageResults.map((r, idx) => (
                  <div key={idx} className="text-xs flex justify-between">
                    <span>{r.ruleName} ({r.ratePercentage} %)</span>
                    <span>{(r.amountCents / 100).toFixed(2)} €</span>
                  </div>
                ))}
              </div>

              <div className="space-y-1">
                <div className="font-semibold text-xs text-[var(--muted-foreground)] uppercase">
                  Kundenseite (Abrechnung Client)
                </div>
                <div className="text-base font-bold">
                  Zuschläge: {(preview.data.billSurchargeCents / 100).toFixed(2)} €
                </div>
                {preview.data.billResults.map((r, idx) => (
                  <div key={idx} className="text-xs flex justify-between">
                    <span>{r.ruleName} ({r.ratePercentage} %)</span>
                    <span>{(r.amountCents / 100).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
